import { FC, ReactNode } from 'react';

import { IRoutingConfigVersion } from '@entities/routing/config-version';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { Stack, Tooltip, Typography } from '@mui/material';
import RoutingVersionStatusBadge from '@shared/components/RoutingVersionStatusBadge';
import { parseUtcDateTime } from '@shared/utils/parseUtcDateTime';

import { ActorLabel } from '../../components/ActorLabel';

interface IProps {
  version: IRoutingConfigVersion;
  // True when this version differs from the one active on prod (shown in the bar above).
  isDiverged?: boolean;
  // Absent while editing or restoring: no pencil then.
  onEditDetails?: () => void;
}

const VersionMetadata: FC<IProps> = ({ version, isDiverged = false, onEditDetails }) => {
  return (
    <section className='routing-config-metadata'>
      <div className='routing-config-metadata-header'>
        <div className='routing-config-metadata-title'>
          <Stack direction='row' spacing={1.5} alignItems='center' flexWrap='wrap'>
            <span className='routing-version-number'>v{version.id}</span>
            <Typography variant='h6' component='h2'>
              {version.name}
            </Typography>
            <RoutingVersionStatusBadge status={version.status} />
            {onEditDetails && (
              <Tooltip title='Edit name and comment'>
                <button
                  type='button'
                  aria-label='Edit name and comment'
                  className='routing-config-metadata-edit-btn'
                  onClick={onEditDetails}
                >
                  <EditOutlinedIcon />
                </button>
              </Tooltip>
            )}
          </Stack>
          {isDiverged && (
            <div className='routing-config-diverged-marker'>not the active version</div>
          )}
        </div>
      </div>

      <dl className='routing-config-metadata-grid'>
        <MetadataItem label='Created by'>
          <ActorLabel actor={version.created_by} />
        </MetadataItem>
        <MetadataItem label='Modified by'>
          <ActorLabel actor={version.updated_by} />
        </MetadataItem>
        <MetadataItem label='Created at'>
          {parseUtcDateTime(version.created_at).toLocaleString()}
        </MetadataItem>
        <MetadataItem label='Published at'>
          {version.published_at ? new Date(version.published_at).toLocaleString() : '—'}
        </MetadataItem>
        {version.parent_version_id !== null && (
          <MetadataItem label='Based on'>{`v${version.parent_version_id}`}</MetadataItem>
        )}
        <MetadataItem label='Comment'>{version.comment || '—'}</MetadataItem>
      </dl>
    </section>
  );
};

export default VersionMetadata;

interface IMetadataItemProps {
  label: string;
  children: ReactNode;
}

const MetadataItem: FC<IMetadataItemProps> = ({ label, children }) => (
  <div className='routing-config-metadata-item'>
    <dt>{label}</dt>
    <dd>{children}</dd>
  </div>
);
